import { cloneDeep } from 'lodash';
import { generateConditionStringFromObj } from '/src/features/widgets/components/data-table/helpers/data-table-display-prerequisite-helper.js';
import { encodeSingleFieldToBase64 } from '/src/common/general-utils';
import { vuetifyOptions } from '/plugins/vuetify.options.js';

export const fieldsToEncode = [
  'AvailableActionsV2',
  'AvailableFilters',
  'DynamicFilter',
  'ExtraInformation',
  'Series',
  'SettingsV2',
  'StyleAttributes',
  'alias',
  'moreinfourl',
  'jsContentV2'
];

function setWidgetProps(widget) {
  if (widget.type === 'Matrix') {
    widget.type = 'Chart';
    widget.Series[0].Type = 'Matrix Chart';
  }

  if (widget.type === 'KPI History') {
    widget.Series[0].Type = 'Line Chart';
    widget.isAnalyzeWidget = true;
  }

  if (widget.type === 'Financial Table') {
    widget.Series[0].Type = 'Financial Table';
  }

  return widget;
}

function setGroupByAndOrderForKpiHistory(widget) {
  if (!/Time|Time~@~/.test(widget.groupBy)) {
    widget.groupBy = 'Time~@~Date';
  }
  if (widget.orderBy !== widget.groupBy) {
    widget.orderBy = widget.groupBy;
    widget.orderByType = 'asc';
  }
  return widget;
}

export const widgetForState = (widget) => {
  if (
    ['Single Value', 'Data Table', 'Chart', 'KPI History', 'World Map', 'Financial Table'].includes(widget.type) &&
    widget.Series.length === 0
  ) {
    widget.Series = [{ KpiRef: null }];
  }

  /**
   * Setting GroupBy and other related value in widget from series which really don't belong to series
   */
  if (widget.Series) {
    if (widget.Series[0]) {
      widget = setWidgetProps(widget);
      let groupByDrilldown;
      try {
        groupByDrilldown = JSON.parse(widget.Series[0].groupByDrilldown) || [];
      } catch (err) {
        groupByDrilldown = [];
      }
      widget = {
        ...widget,
        chartType: widget.Series[0].Type || 'Column Chart',
        groupBy: widget.Series[0].GroupBy || widget.groupby || null,
        groupBy2: widget.Series[0].GroupBy2 || null,
        ...(widget.type !== 'Chart' && { alias: widget.Series[0].Alias }),
        orderBy: widget.Series[0].OrderBy,
        orderByType: widget.Series[0].OrderByType,
        groupByDrilldown,
        limit: widget.Series[0].Limit,
        alias: widget.Series[0].Alias
      };
    }
    if (widget.type === 'KPI History') {
      widget = setGroupByAndOrderForKpiHistory({ ...widget });
    }

    widget.Series = getWidgetSeries(widget);
  }

  return {
    ...widget,
    alias: _parseAlias(widget),
    Settings: _parseSettings(widget),
    enableTitle: widget.enableTitle !== 0,
    moreInfoUrl: _parseMoreInfoUrl(widget),
    AvailableActions: _parseAvailableActions(widget),
    AvailableFilters: _parseAvailableFilters(widget),
    DynamicFilter: _parseDynamicFilters(widget),
    StyleAttributes: _parseStyleAttributes(widget),
    jsContentV2: _parseJsContent(widget)
  };
};
/**
 * sets settings for Api.
 */
const _setSettingsForApi = (widget) => {
  if (widget.Settings.isCollapsible) {
    widget.Settings.isCollapsible = widget.Settings.isCollapsible ? 'Yes' : 'No';
  }
  if (widget.type === 'Print Button') {
    //sets default values.
    let defaultWidth = widget.Settings.printButtonType === 'Form Wizard' ? 315 : 215;
    widget.Settings = {
      ...widget.Settings,
      btnIcon: widget.Settings.btnIcon || 'download',
      pageWidth: widget.Settings.pageWidth || defaultWidth,
      pageHeight: widget.Settings.pageHeight || 594
    };
  }
  if (widget.type === 'Data Table') {
    // Update column order to move fixed columns on start.
    if (widget.Settings?.fields && widget.Settings?.format) {
      const { fixed, unfixed } = widget.Settings.fields.reduce(
        (acc, fieldName) => {
          const fixedType = widget.Settings.format[fieldName]?.isColFixed ? 'fixed' : 'unfixed';
          acc[fixedType].push(fieldName);
          return acc;
        },
        { fixed: [], unfixed: [] }
      );
      widget.Settings.fields = [...fixed, ...unfixed];
    }
  }
  return widget.Settings;
};

export const widgetForApi = (widgetObj) => {
  let widget = cloneDeep(widgetObj);
  widget.SettingsV2 = _setSettingsForApi(widget);
  if (widget.Series) {
    // Resetting the groupBy and other  value in 0th series for backward compatibility
    // we can eliminate this in future
    widget.Series[0] = {
      ...widget.Series[0],
      Type: widget.chartType,
      GroupBy: widget.groupBy,
      GroupBy2: widget.groupBy2,
      ...(widget.type !== 'Chart' && { Alias: JSON.stringify(widget.alias) }),
      OrderBy: widget.orderBy,
      OrderByType: widget.orderByType,
      groupByDrilldown: JSON.stringify(widget.groupByDrilldown),
      Limit: widget.limit
    };

    if (widget.chartType === 'Matrix Chart') {
      widget.type = 'Matrix';
    }
  }
  widget.AvailableActionsV2 = JSON.stringify(widget.AvailableActions);
  delete widget.alias;
  delete widget.Settings;
  delete widget.AvailableActions;

  return {
    ...widget,
    enableTitle: widget.enableTitle ? 1 : 0,
    SettingsV2: JSON.stringify(widget.SettingsV2),
    moreinfourl: JSON.stringify(widget.moreInfoUrl),
    Series: buildSeriesForApi(widget),
    AvailableFilters: buildAvailableFiltersForAPI(widget.AvailableFilters),
    DynamicFilter: widget.type === 'Data Table' ? JSON.stringify(widget.DynamicFilter) : null,
    jsContentV2: widget.type === 'HTML' ? JSON.stringify(widget.jsContentV2) : null,
    StyleAttributes: JSON.stringify(widget.StyleAttributes)
  };
};

/**
 * Basic Setting for newly created widgets
 * So that mapField works properly and we can initialize properties with preferred values
 */
export const widgetBasicSettings = () => ({
  Text: {
    enableMultiLanguage: 1,
    isCollapsible: 'Yes',
    title: '',
    showAsAlert: false
  },
  Cockpit: [],
  'Hierarchical Menu': {
    Configurations: {},
    hierarchicalWidth: 6,
    previewWidth: 6,
    defaultNode: {}
  }
});

export const getExportRequestData = (widget) => {
  let queryParams = [`noLimit=true`];

  if (widget.groupBy && widget.orderBy) {
    queryParams = queryParams.concat([
      `groupBy=${encodeURIComponent(widget.groupBy)}`,
      `orderBy=${encodeURIComponent(widget.orderBy)}`,
      `orderByType=${encodeURIComponent(widget.orderByType)}`
    ]);
  } else if (widget.type.toLowerCase() === 'data table' && widget.orderBy) {
    queryParams = queryParams.concat([
      `orderBy=${encodeURIComponent(widget.orderBy)}`,
      `orderByType=${encodeURIComponent(widget.orderByType)}`
    ]);
  }

  /**
   * TODO: Change this condition based on how are we going to configure the URL filters for vue
   */
  let reqBody = { title: encodeSingleFieldToBase64(widget.title) };
  if (widget._filters && widget._filters[0]) {
    reqBody['filters'] = encodeSingleFieldToBase64(JSON.stringify(widget._filters));
  }
  if (widget.inlineFilter) {
    reqBody['inlineFilter'] = encodeSingleFieldToBase64(widget.inlineFilter);
  }
  if (widget.whereClause) {
    reqBody['exportWhereClause'] = encodeSingleFieldToBase64(widget.whereClause);
    reqBody['fieldsMap'] = encodeSingleFieldToBase64(widget.fieldsMap);
  }
  if (widget.auditTrailSettings) {
    reqBody['auditTrailSettings'] = encodeSingleFieldToBase64(JSON.stringify(widget.auditTrailSettings));
  }

  return { queryParams, reqBody };
};

const getWidgetSeries = (widget) => {
  let orderedSeries = widget.Series;
  return orderedSeries.map((series) => {
    let Settings = series.Settings ? series.Settings.sParseJSON() : {};
    return { ...series, Settings };
  });
};

/**
 * Parses widget settings
 * @param {Object} widget widget
 */
const _parseSettings = (widget) => {
  let settings = {};
  try {
    settings = JSON.parse(widget.SettingsV2) || {};
  } catch (err) {
    appInsights.trackException({
      exception: err,
      properties: {
        feature: 'Sofy widgets',
        action: 'Widget settings JSON is not parsable' + JSON.stringify(widget)
      }
    });
  }
  settings = _getTypeSpecificSettings(widget, settings);
  return settings;
};

/**
 * Adds settings specific to widget type
 */
const _getTypeSpecificSettings = (widget, settings) => {
  switch (widget.type) {
    case 'Data Table':
      settings.fields = _getSettingsValue(settings.fields, []);
      settings.format = _getSettingsValue(settings.format, {});
      settings.selectedFormat = _getSettingsValue(settings.selectedFormat, {});
      settings.formatBydate = _getSettingsValue(settings.formatBydate, []);
      settings.attachmentDetails = _getSettingsValue(settings.attachmentDetails, {});
      settings.showRowSummary = _getSettingsValue(settings.showRowSummary, { columns: [] });
      settings.unfoldableRowSettings = _getSettingsValue(settings.unfoldableRowSettings, {});
      /**
       * This prepares condition string from the object which is data table display prerequisite (need to calculate before rendering)
       * ( Not related to vue-angular compatibility ) Also required to keep after Migration Script
       */
      settings.unfoldableRowSettings.conditionalTemplates = generateConditionStringFromObj(
        settings.unfoldableRowSettings.conditionalTemplates
      );
      break;
    case 'Chart':
    case 'KPI History':
      settings.orderLabelColor = settings.orderLabelColor?.length ? settings.orderLabelColor : [{}];
      settings.reverseAxis = _getSettingsValue(settings.reverseAxis, {});
      settings.showDecimalAxes = settings.showDecimalAxes === false ? false : true;
      break;
    case 'World Map':
      settings.worldMapSeriesColor = _getSettingsValue(settings.worldMapSeriesColor, {
        defaultColor: vuetifyOptions.theme.themes.light.colors.black,
        rules: null
      });
      settings.coordinateConfig = _getSettingsValue(settings.coordinateConfig, {});
      break;
    case 'Print Button':
      settings.printButtonType = _getSettingsValue(settings.printButtonType, 'Dashboard');
      settings.btnSize = _getSettingsValue(settings.btnSize, 'btn-md');
      break;
    case 'Hierarchical Menu':
      settings.defaultNode = _getSettingsValue(settings.defaultNode, {});
      break;
    case 'Process Flow':
      settings.goData = _getSettingsValue(settings.goData, {
        nodeDataArray: [],
        linkDataArray: []
      });
      break;
    case 'Text':
      settings.isCollapsible = settings.isCollapsible === 'Yes';
      break;
  }
  return settings;
};

/**
 * Parses widget moreInfoUrl
 * @param {Object} widget widget
 */
const _parseMoreInfoUrl = (widget) => {
  let moreInfoUrl;
  try {
    moreInfoUrl = JSON.parse(widget.moreinfourl) || {};
  } catch (err) {
    moreInfoUrl = {
      state: widget.moreinfourl
    };
  }
  return moreInfoUrl;
};

/**
 * Parses widget available actions
 * @param {Object} widget widget
 */
const _parseAvailableActions = (widget) => {
  let actions = [];
  try {
    actions = JSON.parse(widget.AvailableActionsV2) || [];
  } catch (error) {
    appInsights.trackException({
      exception: error,
      properties: {
        feature: 'Sofy widgets',
        action: 'parse widget action',
        data: JSON.stringify(widget)
      }
    });
  }
  return actions;
};

/**
 * Parses widget available filters
 * @param {Object} widget widget
 */
const _parseAvailableFilters = (widget) => {
  let availableFilters = [];
  try {
    availableFilters = JSON.parse(widget.AvailableFilters) || [];

    availableFilters.map((filter) => {
      filter.id = Math.random().toString(36).slice(2, 11);
      if (filter.queryBuilder) {
        filter.queryBuilder = {
          condition: filter.queryBuilder.Conditions.group.operator,
          rules: filter.queryBuilder.Conditions.group.rules.map((rule) => ({
            ...rule,
            key: rule.field ? rule.field.name : rule.key,
            operator: rule.condition,
            value: rule.data1
          }))
        };
      }
      if (widget.type === 'Filter Bar') {
        filter.sortDesc = filter.sortDesc || 'No';
      }
    });
  } catch (error) {
    appInsights.trackException({
      exception: error,
      properties: {
        feature: 'Sofy widgets',
        action: 'parse widget available filters',
        data: JSON.stringify(widget)
      }
    });
  }
  return availableFilters;
};

/**
 * Parses widget dynamic filters
 * @param {Object} widget widget
 */
const _parseDynamicFilters = (widget) => {
  let dynamicFilter = {};

  try {
    dynamicFilter = JSON.parse(widget.DynamicFilter) || {
      dynamicFiltering: false,
      dynamicFilterConfig: { OrderByType: 'asc', showNoValue: 'No', showAllFilter: 'No' }
    };
  } catch (error) {
    appInsights.trackException({
      exception: error,
      properties: {
        feature: 'Sofy widgets',
        action: 'parse widget dynamic filter',
        data: JSON.stringify(widget)
      }
    });
  }
  return dynamicFilter;
};

const _parseStyleAttributes = (widget) => {
  let styleAttributes = {};
  try {
    styleAttributes = JSON.parse(widget.StyleAttributes) || {};
  } catch (error) {
    appInsights.trackException({
      exception: error,
      properties: {
        feature: 'Sofy widgets',
        action: 'parse widget style attributes',
        data: JSON.stringify(widget)
      }
    });
  }
  return styleAttributes;
};

/**
 * Parses widget available actions
 * @param {Object} widget widget
 */
const _parseJsContent = (widget) =>
  widget.jsContentV2 ? widget.jsContentV2.sParseJSON() : { template: '', style: '', script: '' };
/**
 * Parses widget available filters
 * @param {Object} availableFilters configuration of the manual filter
 */

const buildAvailableFiltersForAPI = (availableFilters) => {
  if (availableFilters) {
    availableFilters.map((filter) => {
      if (filter.queryBuilder) {
        filter.queryBuilder = {
          Conditions: {
            group: {
              operator: filter.queryBuilder.condition,
              rules: filter.queryBuilder.rules.map((rule) => ({
                ...rule,
                data1: rule.value,
                condition: rule.operator,
                field: { name: rule.key }
              }))
            }
          }
        };
      }
    });
  }
  return JSON.stringify(availableFilters);
};

const _parseAlias = (widget) => {
  let alias = {};
  try {
    alias = JSON.parse(widget.alias) || {};
  } catch (error) {
    appInsights.trackException({
      exception: error,
      properties: {
        feature: 'Sofy widgets',
        action: 'parse widget alias',
        data: JSON.stringify(widget)
      }
    });
  }
  return alias;
};

const buildSeriesForApi = (widget) =>
  widget.Series
    ? widget.Series.map(({ kpiName, businessRuleName, KpiTitle, ...series }) => ({
        ...series,
        BusinessRuleRef: series.BusinessRuleRef ? series.BusinessRuleRef : widget.BusinessRuleRef,
        Settings: JSON.stringify(series.Settings),
        ShowNumbers: series.Settings ? series.Settings.ShowNumbers : false
      }))
    : undefined;

export const getParsedFields = (fields, objectFields) => {
  const objectFieldMap = objectFields.reduce((acc, f) => ({ ...acc, [f.Name]: { uuid: f.UUID, name: f.Name } }), {});
  return fields.reduce((acc, fieldName) => {
    if (objectFieldMap[fieldName]) {
      acc.push(objectFieldMap[fieldName]);
    }
    return acc;
  }, []);
};

const _getSettingsValue = (primaryValue, secondaryValue) => primaryValue || secondaryValue;
